import { Reveal, SectionHead } from "@/components/Reveal";
import { SYSTEM_PARTS } from "@/data/content";
import { PRODUCTS } from "@/data/images";
import { Camera, Network, MonitorPlay, BellRing, Dot } from "lucide-react";

const ICON_MAP = { camera: Camera, network: Network, monitor: MonitorPlay, bell: BellRing };

const CAMERA_TYPES = [
  {
    image: PRODUCTS.bulletSide,
    title: "Thermische bulletcamera",
    text: "Vaste thermische bewaking van een specifiek risicogebied of opslagvak.",
    alt: "Hikvision thermische bulletcamera, zijaanzicht",
  },
  {
    image: PRODUCTS.bulletFront,
    title: "Bi-spectrum bulletcamera",
    text: "Thermisch én optisch beeld in één camera voor snellere visuele verificatie.",
    alt: "Hikvision bi-spectrum bulletcamera met thermische en optische lens",
  },
  {
    image: PRODUCTS.ptzDome,
    title: "Thermische PTZ-camera",
    text: "Beweegbare bi-spectrumcamera voor grotere terreinen en meerdere zones.",
    alt: "Hikvision thermische PTZ speed dome camera",
  },
  {
    image: PRODUCTS.exProof,
    title: "Explosieveilige uitvoering",
    text: "RVS Ex-behuizing voor zones met verhoogd explosiegevaar (ATEX-omgevingen).",
    alt: "Hikvision explosieveilige thermische camera in RVS behuizing",
  },
];

export const SystemSchema = () => (
  <section className="py-24 md:py-36" data-testid="system-schema-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <SectionHead nr="05" overline="Onderdelen van een oplossing" title="Een complete thermische detectieoplossing" />

      <div className="mt-14 grid gap-10 lg:grid-cols-[0.9fr_1.6fr] lg:gap-16">
        <Reveal className="relative">
          <div className="sticky top-28 overflow-hidden rounded-2xl border border-white/10 bg-[#0c1319]">
            <div
              className="relative flex aspect-[3/4] items-center justify-center p-8"
              style={{ background: "radial-gradient(circle at 50% 38%, rgba(43,159,215,0.16), transparent 62%)" }}
            >
              <img
                src={PRODUCTS.bulletFront}
                alt="Hikvision bi-spectrum thermische bulletcamera voor industriële branddetectie"
                className="max-h-full w-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="scan-line" aria-hidden="true" />
            <p className="border-t border-white/10 p-4 text-xs leading-relaxed text-[#CBD3D9]/80">
              Wij ontwerpen en installeren Hikvision thermische camerasystemen. Camera en lens worden
              per project berekend.
            </p>
          </div>
        </Reveal>

        <div className="grid gap-6 sm:grid-cols-2">
          {SYSTEM_PARTS.map((part, i) => {
            const Icon = ICON_MAP[part.icon];
            return (
              <Reveal key={part.title} delay={i * 0.1} className="h-full">
                <article className="h-full rounded-2xl border border-white/10 bg-[#10232F]/50 p-6 backdrop-blur-md transition-[border-color] duration-500 hover:border-[#62C3F0]/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#2B9FD7]/30 bg-[#2B9FD7]/10 text-[#62C3F0]">
                    <Icon size={20} strokeWidth={1.6} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-[#F5F7F9]">{part.title}</h3>
                  <ul className="mt-3 grid gap-1.5">
                    {part.items.map((item) => (
                      <li key={item} className="flex items-start gap-1 text-sm leading-relaxed text-[#CBD3D9]/85">
                        <Dot size={18} className="mt-0.5 shrink-0 text-[#2B9FD7]" /> {item}
                      </li>
                    ))}
                  </ul>
                  {part.note && <p className="mt-3 text-xs italic text-[#CBD3D9]/60">{part.note}</p>}
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>

      <div className="mt-20" data-testid="camera-types">
        <Reveal>
          <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-[#62C3F0]">
            Hikvision-technologie voor thermische detectie
          </p>
          <h3 className="mt-3 font-heading text-xl font-bold tracking-tight text-[#F5F7F9] md:text-2xl">
            Het juiste cameratype per risicogebied
          </h3>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {CAMERA_TYPES.map((cam, i) => (
            <Reveal key={cam.title} delay={(i % 4) * 0.08} className="h-full">
              <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0c1319] transition-[border-color,transform] duration-500 hover:-translate-y-1.5 hover:border-[#62C3F0]/40">
                <div
                  className="relative flex h-52 items-center justify-center p-6"
                  style={{ background: "radial-gradient(circle at 50% 45%, rgba(43,159,215,0.14), transparent 65%)" }}
                >
                  <img
                    src={cam.image}
                    alt={cam.alt}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col border-t border-white/10 p-5">
                  <h4 className="font-heading text-sm font-bold text-[#F5F7F9]">{cam.title}</h4>
                  <p className="mt-2 text-xs leading-relaxed text-[#CBD3D9]/80">{cam.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-xs leading-relaxed text-[#CBD3D9]/60">
            Getoonde camera's zijn voorbeelden van mogelijke uitvoeringen. Het definitieve cameratype,
            de lens en de configuratie worden per project bepaald op basis van de locatieanalyse.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <p className="mt-10 max-w-3xl border-l-2 border-[#FF8A00]/50 pl-5 text-sm leading-relaxed text-[#CBD3D9]/70">
          De gewenste koppelingen worden vooraf technisch beoordeeld. Niet iedere functie is
          beschikbaar op ieder cameramodel of iedere installatie.
        </p>
      </Reveal>
    </div>
  </section>
);
